/**
 * What is using a machine (ADR 0063 step 4), in the console's words: the hub module
 * 'machine-load' sends shares per lease, session, project and background kind; this file names
 * them and folds them for the share bar and the Overview tile. Pure, so it is tested without React.
 */
import { LOAD_KIND_LABEL, MACHINE_LOAD_TTL_MS } from '@orgus/protocol';
import type { Id, LoadGroup, LoadKind, Machine, MachineLoadEntry, MachineLoadState, Project, Session } from '@orgus/protocol';
import type { DataSlot, ShareBarPart } from '@orgus/ui';

export const MACHINE_LOAD_STATE_EXT = 'machine-load';

/** The share bar's parts, in slot order: the same slot for the same part on every machine. */
export const LOAD_PARTS: readonly { key: 'work' | 'sessions' | 'projects' | 'background' | 'shortLived'; label: string; slot?: DataSlot; other?: boolean }[] = [
  { key: 'work', label: 'Heavy work', slot: 1 },
  { key: 'sessions', label: 'Sessions', slot: 2 },
  { key: 'projects', label: 'Projects', slot: 3 },
  { key: 'background', label: 'Background', slot: 4 },
  { key: 'shortLived', label: 'Short-lived', other: true },
];

/** The share bar's slot for a group's kind, so a driver's bar matches its part of the share bar. */
export function slotOfKind(kind: LoadKind): DataSlot {
  return kind === 'work' ? 1 : kind === 'session' ? 2 : kind === 'project' ? 3 : 4;
}

/** The parts with a share (an empty part would only add a 0% to the legend). */
export function shareParts(e: Pick<MachineLoadEntry, 'totals'>): ShareBarPart[] {
  return LOAD_PARTS.filter((p) => (e.totals[p.key] ?? 0) >= 0.5).map((p) => ({ key: p.key, label: p.label, value: (e.totals[p.key] ?? 0) / 100, ...(p.slot ? { slot: p.slot } : {}), ...(p.other ? { other: true } : {}) }));
}

/** "Heavy work 41%, background 20%, idle 39%" for the bar's accessible name. */
export function shareSummary(name: string, e: Pick<MachineLoadEntry, 'totals' | 'load'>): string {
  const pct = (n: number) => `${Math.round(n)}%`;
  const parts = LOAD_PARTS.filter((p) => (e.totals[p.key] ?? 0) >= 1).map((p) => `${p.label.toLowerCase()} ${pct(e.totals[p.key])}`);
  return `CPU of ${name}: ${[...parts, `idle ${pct((1 - e.load) * 100)}`].join(', ')}`;
}

interface Names {
  sessions: Record<Id, Pick<Session, 'title'>>;
  projects: Record<Id, Pick<Project, 'name'>>;
}

/** A group's name: the command a lease runs, the session's title, the project, or the kind. */
export function groupName(g: LoadGroup, n: Names): string {
  switch (g.kind) {
    case 'work':
      return g.label;
    case 'session':
      return (g.sessionId && n.sessions[g.sessionId]?.title) || 'A session';
    case 'project':
      return (g.projectId && n.projects[g.projectId]?.name) || g.label;
    default:
      return LOAD_KIND_LABEL[g.kind];
  }
}

/** What runs inside a group, quietly after its name: 'vitest, tsc'. Background kinds name their tools too ('Defender'). */
export function groupTools(g: LoadGroup): string {
  if (g.kind === 'session') return '';
  return g.kind === 'short-lived' ? 'too brief to name' : (g.tools ?? []).filter((t) => t !== g.label).join(', ');
}

/** Whether an entry is recent enough to show as live. */
export const fresh = (e: Pick<MachineLoadEntry, 'at'>, now: number): boolean => now - e.at <= MACHINE_LOAD_TTL_MS;

export function entryFor(s: MachineLoadState | undefined, machineId: Id): MachineLoadEntry | undefined {
  return s?.machines.find((e) => e.machineId === machineId);
}

/** Slots free for heavy work, and how many more default-size heavy commands fit now. */
export function headroom(w: NonNullable<MachineLoadEntry['work']>): { free: number; fit: number } {
  const free = Math.max(0, w.budget - w.used);
  const want = Math.max(2, Math.ceil(w.budget / 3));
  return { free, fit: w.waiting.length ? 0 : Math.floor(free / want) };
}

export interface Driver {
  key: string;
  kind: LoadKind;
  name: string;
  /** Share of every online machine's CPU together, 0..100. */
  pct: number;
  machineId: Id;
}

/**
 * The top drivers of load across the online machines, as shares of all their CPUs together: a
 * 32 core machine's 40% outweighs a laptop's. Background kinds add up across machines; a lease,
 * session or project stays its own.
 */
export function fleetDrivers(s: MachineLoadState | undefined, machines: readonly Pick<Machine, 'id' | 'cpus' | 'online'>[], n: Names, now: number, max = 3): Driver[] {
  const on = new Map(machines.filter((m) => m.online).map((m) => [m.id, m]));
  const cpus = [...on.values()].reduce((a, m) => a + Math.max(1, m.cpus), 0);
  if (!s || !cpus) return [];
  const by = new Map<string, Driver>();
  for (const e of s.machines) {
    const m = on.get(e.machineId);
    if (!m || !fresh(e, now)) continue;
    const weight = Math.max(1, m.cpus) / cpus;
    for (const g of e.groups) {
      const key = g.kind === 'work' ? `work:${g.leaseId ?? g.label}` : g.kind === 'session' ? `session:${g.sessionId}` : g.kind === 'project' ? `project:${g.projectId}` : `kind:${g.kind}`;
      const d = by.get(key) ?? { key, kind: g.kind, name: groupName(g, n), pct: 0, machineId: e.machineId };
      d.pct += g.pct * weight;
      by.set(key, d);
    }
  }
  return [...by.values()]
    .map((d) => ({ ...d, pct: Math.round(d.pct * 10) / 10 }))
    .filter((d) => d.pct >= 1)
    .sort((a, b) => b.pct - a.pct || a.name.localeCompare(b.name))
    .slice(0, max);
}

const KIND_WORD: Record<LoadKind, string> = {
  work: 'heavy work',
  session: 'sessions',
  project: 'projects',
  editor: 'editors',
  antivirus: 'antivirus',
  indexer: 'search indexing',
  browser: 'browsers',
  system: 'Windows and services',
  orgus: 'Orgus',
  other: 'other apps',
  'short-lived': 'short-lived processes',
};

/** "heavy work", "antivirus": what kind of thing a driver is, for "37% load, mostly heavy work". */
export const kindWord = (k: LoadKind): string => KIND_WORD[k];

/** "pnpm test 34%, Antivirus 12%, Editors 8%". */
export const driversText = (d: readonly Driver[]): string => d.map((x) => `${x.name} ${Math.round(x.pct)}%`).join(', ');
